import { useState, useEffect } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Shield, ShieldCheck, ShieldOff, Copy, Check, KeyRound, RefreshCw } from "lucide-react";
import RecoveryCodesDialog from "@/components/RecoveryCodesDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TwoFactorDialog({ open, onClose, user, onChanged }) {
  const [stage, setStage] = useState("idle"); // idle | setup | disable | regenerate
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [recoveryStatus, setRecoveryStatus] = useState(null); // {total, remaining}

  const enabled = !!user?.totp_enabled;

  useEffect(() => {
    if (open && enabled) {
      axios.get(`${API}/auth/2fa/recovery-codes/status`, { withCredentials: true })
        .then(({ data }) => setRecoveryStatus(data))
        .catch(() => {});
    } else {
      setRecoveryStatus(null);
    }
  }, [open, enabled]);

  const reset = () => {
    setStage("idle");
    setSetupData(null);
    setCode("");
    setError("");
    setCopied(false);
    setLoading(false);
    setRecoveryCodes(null);
  };

  const handleClose = () => {
    if (recoveryCodes) return; // wait until recovery dialog closed
    reset(); onClose();
  };

  const startSetup = async () => {
    setError(""); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/setup`, {}, { withCredentials: true });
      setSetupData(data);
      setStage("setup");
    } catch (err) {
      setError(err.response?.data?.detail || "Setup fehlgeschlagen");
    } finally { setLoading(false); }
  };

  const confirmEnable = async () => {
    setError(""); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/enable`, { code }, { withCredentials: true });
      if (onChanged) await onChanged();
      setRecoveryCodes(data.recovery_codes || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Code falsch");
    } finally { setLoading(false); }
  };

  const startRegenerate = () => { setStage("regenerate"); setCode(""); setError(""); };

  const confirmRegenerate = async () => {
    setError(""); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/2fa/recovery-codes/regenerate`, { code }, { withCredentials: true });
      setRecoveryCodes(data.recovery_codes || []);
      setStage("idle");
      setCode("");
      // Refresh remaining count
      try {
        const status = await axios.get(`${API}/auth/2fa/recovery-codes/status`, { withCredentials: true });
        setRecoveryStatus(status.data);
      } catch {}
    } catch (err) {
      setError(err.response?.data?.detail || "Code falsch");
    } finally { setLoading(false); }
  };

  const startDisable = () => { setStage("disable"); setCode(""); setError(""); };

  const confirmDisable = async () => {
    setError(""); setLoading(true);
    try {
      await axios.post(`${API}/auth/2fa/disable`, { code }, { withCredentials: true });
      if (onChanged) await onChanged();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Code falsch");
    } finally { setLoading(false); }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(setupData.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="bg-[#12202f] border-[#3A4F60]/50 text-white max-w-lg" aria-describedby="twofa-dialog-desc">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            {enabled ? <ShieldCheck className="w-5 h-5 text-green-400" /> : <Shield className="w-5 h-5 text-[#C5993A]" />}
            Zwei-Faktor-Authentifizierung
          </DialogTitle>
          <p id="twofa-dialog-desc" className="sr-only">2FA Einstellungen</p>
        </DialogHeader>

        <div className="py-2 space-y-4" data-testid="twofa-content">
          {stage === "idle" && (
            <div className="space-y-4">
              <div className={`p-4 rounded border ${enabled ? "border-green-400/30 bg-green-400/5" : "border-amber-400/30 bg-amber-400/5"}`}>
                <p className="text-sm">
                  Status: {enabled ? (
                    <span className="text-green-400 font-medium">Aktiviert</span>
                  ) : (
                    <span className="text-amber-400 font-medium">Nicht aktiviert</span>
                  )}
                </p>
                <p className="text-xs text-[#8899AA] mt-2">
                  {enabled
                    ? "Beim Login wird zusätzlich zum Passwort ein 6-stelliger Code aus Microsoft Authenticator (oder einer anderen TOTP-App) abgefragt."
                    : "Schützen Sie Ihr Konto mit einem zweiten Faktor. Sie benötigen Microsoft Authenticator, Google Authenticator oder eine ähnliche TOTP-App."}
                </p>
              </div>

              {!enabled ? (
                <Button
                  data-testid="twofa-start-setup"
                  onClick={startSetup}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0"
                >
                  {loading ? "Lädt..." : "2FA einrichten"}
                </Button>
              ) : (
                <div className="space-y-2">
                  {recoveryStatus && (
                    <div className="bg-[#0C1620] border border-[#3A4F60]/40 rounded p-3 flex items-center justify-between" data-testid="recovery-status">
                      <div className="flex items-center gap-2">
                        <KeyRound className="w-4 h-4 text-[#C5993A]" />
                        <span className="text-sm text-white">
                          {recoveryStatus.remaining} / {recoveryStatus.total} Recovery-Codes übrig
                        </span>
                      </div>
                      <Button
                        data-testid="twofa-start-regenerate"
                        onClick={startRegenerate}
                        variant="ghost"
                        size="sm"
                        className="text-[#C5993A] hover:text-white hover:bg-[#C5993A] h-8"
                      >
                        <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Neu generieren
                      </Button>
                    </div>
                  )}
                  <Button
                    data-testid="twofa-start-disable"
                    onClick={startDisable}
                    variant="ghost"
                    className="w-full text-red-400 hover:text-red-300 border border-red-400/30 hover:bg-red-400/10"
                  >
                    <ShieldOff className="w-4 h-4 mr-2" /> 2FA deaktivieren
                  </Button>
                </div>
              )}
            </div>
          )}

          {stage === "setup" && setupData && (
            <div className="space-y-4">
              <ol className="text-sm text-[#8899AA] space-y-2 list-decimal list-inside">
                <li>Öffnen Sie <strong className="text-white">Microsoft Authenticator</strong> auf Ihrem Smartphone.</li>
                <li>Tippen Sie auf „+" → „Anderes Konto" und scannen Sie den QR-Code.</li>
                <li>Geben Sie den 6-stelligen Code unten ein, um die Einrichtung abzuschließen.</li>
              </ol>

              <div className="flex justify-center bg-white p-4 rounded-lg">
                <QRCodeSVG value={setupData.otpauth_uri} size={200} level="M" />
              </div>

              <div className="bg-[#0C1620] border border-[#3A4F60]/40 rounded p-3">
                <p className="text-[10px] text-[#3A4F60] uppercase tracking-wider mb-1">Secret manuell (falls QR nicht funktioniert)</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs text-[#C5993A] font-mono break-all flex-1" data-testid="twofa-secret">{setupData.secret}</code>
                  <Button onClick={copySecret} variant="ghost" size="sm" className="h-7 text-[#8899AA] hover:text-[#C5993A] flex-shrink-0">
                    {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>

              <div>
                <label className="block text-sm text-[#8899AA] mb-1.5">6-stelliger Code aus der App</label>
                <Input
                  data-testid="twofa-verify-code"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-center text-xl tracking-[0.4em] focus:border-[#C5993A]"
                />
              </div>

              {error && <p data-testid="twofa-error" className="text-red-400 text-sm">{error}</p>}
            </div>
          )}

          {stage === "disable" && (
            <div className="space-y-4">
              <p className="text-sm text-[#8899AA]">
                Bitte geben Sie den aktuellen 6-stelligen Code aus Ihrer Authenticator-App ein, um 2FA zu deaktivieren.
              </p>
              <Input
                data-testid="twofa-disable-code"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
                className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-center text-xl tracking-[0.4em] focus:border-[#C5993A]"
              />
              {error && <p data-testid="twofa-error" className="text-red-400 text-sm">{error}</p>}
            </div>
          )}

          {stage === "regenerate" && (
            <div className="space-y-4">
              <p className="text-sm text-[#8899AA]">
                Bestätigen Sie mit Ihrem aktuellen Authenticator-Code, um neue Recovery-Codes zu generieren. <strong className="text-white">Die alten Codes werden ungültig.</strong>
              </p>
              <Input
                data-testid="twofa-regen-code"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
                className="bg-[#0C1620] border-[#3A4F60]/50 text-white text-center text-xl tracking-[0.4em] focus:border-[#C5993A]"
              />
              {error && <p data-testid="twofa-error" className="text-red-400 text-sm">{error}</p>}
            </div>
          )}
        </div>

        <DialogFooter>
          {stage === "idle" ? (
            <Button onClick={handleClose} variant="ghost" className="text-[#8899AA]">Schließen</Button>
          ) : stage === "setup" ? (
            <>
              <Button onClick={handleClose} variant="ghost" className="text-[#8899AA]">Abbrechen</Button>
              <Button
                data-testid="twofa-confirm-enable"
                onClick={confirmEnable}
                disabled={loading || code.length !== 6}
                className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0"
              >
                {loading ? "Prüfe..." : "Aktivieren"}
              </Button>
            </>
          ) : stage === "regenerate" ? (
            <>
              <Button onClick={() => setStage("idle")} variant="ghost" className="text-[#8899AA]">Zurück</Button>
              <Button
                data-testid="twofa-confirm-regenerate"
                onClick={confirmRegenerate}
                disabled={loading || code.length !== 6}
                className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0"
              >
                {loading ? "Prüfe..." : "Neu generieren"}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setStage("idle")} variant="ghost" className="text-[#8899AA]">Zurück</Button>
              <Button
                data-testid="twofa-confirm-disable"
                onClick={confirmDisable}
                disabled={loading || code.length !== 6}
                className="bg-red-500 hover:bg-red-600 text-white border-0"
              >
                {loading ? "Prüfe..." : "Deaktivieren"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
      <RecoveryCodesDialog
        open={!!recoveryCodes}
        codes={recoveryCodes || []}
        onClose={() => { setRecoveryCodes(null); reset(); onClose(); }}
      />
    </Dialog>
  );
}
